import { IconButton } from '@civilgrid/ui/components';
import { useNavigate, useParams, useRouter } from '@tanstack/react-router';
import { useSnapshot } from 'valtio';
import Icon from '~/components2/Icon';
import {
  historyEntryKeySelector,
  layerLibraryReturnKey,
  saveReturnViewport,
} from '~/components2/Map/helpers/layerLibraryReturnViewport';
import { mapState } from '~/components2/Map/Map';
import { DEFAULT_LAYER_LIBRARY_TAB } from '~/pages/layerLibrary/helpers';
import rootStore from '~/store';

// Entry points into the Layer Library, both living in the layer picker (TOC). Hidden for
// unverified users, who must not browse/add the Library's utility and special-dataset
// layers (the page itself also redirects them, see resolveLayerLibraryAccess).

function useOpenLayerLibrary() {
  const navigate = useNavigate();
  const router = useRouter();
  const { projectId, mapId } = useParams({ strict: false }) as { projectId?: string; mapId?: string };
  return () => {
    // Stash the live camera, keyed to this history entry, so navigating back restores
    // it. CivilDraw (routes with a $mapId) persists its viewport via localStorage instead.
    if (!mapId) {
      saveReturnViewport(layerLibraryReturnKey(projectId), historyEntryKeySelector(router.history), mapState.viewState);
    }
    navigate({ to: '/settings/layerLibrary/$tabId', params: { tabId: DEFAULT_LAYER_LIBRARY_TAB } });
  };
}

function useLayerLibraryEntryVisible() {
  const { inUnverifiedMode } = useSnapshot(rootStore);
  return !inUnverifiedMode;
}

/** "Library" button in the TOC panel header (Figma 631:13509). */
export function LayerLibraryButton() {
  const visible = useLayerLibraryEntryVisible();
  const openLibrary = useOpenLayerLibrary();

  if (!visible) return null;

  return (
    <IconButton icon="layers" variant="secondary" data-testid="layer-library-open" onClick={openLibrary}>
      Library
    </IconButton>
  );
}

/** "Add more layers from the Layer Library" link at the bottom of the TOC list (Figma 631:13531). */
export function LayerLibraryAddLink() {
  const visible = useLayerLibraryEntryVisible();
  const openLibrary = useOpenLayerLibrary();

  if (!visible) return null;

  return (
    <button
      type="button"
      data-testid="layer-library-add-link"
      onClick={openLibrary}
      className="flex w-full items-center justify-center gap-1 rounded px-2 py-1 text-xs font-medium text-blue-50 hover:bg-grey-10 cursor-pointer"
    >
      <Icon icon="plus" size={14} />
      <span>
        Add more layers from the <span className="font-semibold">Layer Library</span>
      </span>
    </button>
  );
}
